import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {ToastrService} from 'ngx-toastr';
import {Subject, Subscription} from 'rxjs';
import {BusinessDataService} from '../../data-repositories/business-data.service';
import {LoginDataService} from '../../data-repositories/login-data.service';
import {User} from '../../models/user';
import {AuthService} from '../../repositories/auth.service';
import {StagesNavigationService} from '../workspace/stages-navigation/stages-navigation.service';
import {GoogleTagManagerService} from '../../services/google-tag-manager.service';
import {takeUntil} from 'rxjs/operators';
import {BrowserStorageService} from '../../services/browser-storage.service';

@Component({
  standalone: false,
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  destroy = new Subject();
  @Output() localizeItem: EventEmitter<any> = new EventEmitter();
  public isWaitingResponse = false;
  public loginModel = new User();
  public tries: number;
  public charCodeValue = '';
  private recaptchaV3ServiceSubscription: Subscription;

  constructor(
    private loginService: LoginDataService,
    private authService: AuthService,
    private service: BusinessDataService,
    private router: Router,
    private route: ActivatedRoute,
    public toastr: ToastrService,
    private translateService: TranslateService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private stagesNavigationService: StagesNavigationService,
    private googleTagManagerService: GoogleTagManagerService,
    private browseStorage: BrowserStorageService,
  ) {
    this.loginModel.verificationCode = '';
    this.loginModel.phone = this.authService.phone;
    this.loginModel.vid = parseInt(this.authService.vid);

    this.route.queryParams
      .pipe(takeUntil(this.destroy))
      .subscribe(params => {
        const isSaveUTMParams = Object.keys(params).filter(x => x.startsWith('utm_')).length > 0;

        if (isSaveUTMParams) {
          this.loginModel.userUTMParameters = {
            utmSource: params.utm_source ? params.utm_source : '',
            utmMedium: params.utm_medium ? params.utm_medium : '',
            utmCampaign: params.utm_campaign ? params.utm_campaign : ''
          };
        }
      });
  }

  public get newCodeWasSent(): boolean {
    const value = sessionStorage.getItem('newCodeWasSent');
    return value && value === 'true';
  }

  public set newCodeWasSent(value: boolean) {
    sessionStorage.setItem('newCodeWasSent', value.toString());
  }

  get videoTutorialSrc(): string {
    const tutorialKey = this.stagesNavigationService.isMobile
      ? 'VideoTutorialMobile.Confirmation'
      : 'VideoTutorial.Confirmation';

    const value = this.translateService.instant(tutorialKey);
    return (value === tutorialKey ? null : value);
  }

  ngOnDestroy(): void {
    this.destroy.complete();

    if (this.recaptchaV3ServiceSubscription) {
      this.recaptchaV3ServiceSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.tries = 2;
    this.localizeItem.emit({
      header: 'Confirmation form',
      description:
        'Email with veryfication code was sent to email ' +
        this.authService.email,
    });
  }

  public trySendCode(code: string) {
    if (code && code.length === 4) {
      this.loginModel.verificationCode = code;
      this.confirm();
    }
  }

  public confirm(): void {
    if (!this.loginModel.verificationCode) {
      this.toastr.warning(this.translateService.instant('Confirmation.Errors.EmptyCode'),
        this.translateService.instant('Confirmation.Errors.Warning'));
      return;
    }

    const reg = /^\d+$/;
    if (!reg.test(String(this.loginModel.verificationCode).toLowerCase())) {
      this.toastr.warning('Confirmation.Errors.EmptyCodeFormat',
        this.translateService.instant('Confirmation.Errors.Warning'));
      return;
    }

    this.loginModel.partnerKey = localStorage.getItem('partner');
    this.isWaitingResponse = true;
    this.loginService.verifyUser(
      this.loginModel,
      (user) => {
        this.isWaitingResponse = false;
        if (user.accessToken) {
          this.googleTagManagerService.submit(this.loginModel);

          if (user.applicationStatus === 'Success') {
            document.location.href =
              'https://www.clientam.com/sso/Login?partnerID=Mexemltd.';
          } else if (
            user.applicationStatus === 'Validation' ||
            user.applicationStatus === 'AccountPending' ||
            user.applicationStatus === 'WaitingForApprove'
          ) {
            this.authService.login(user.accessToken);
            this.authService.setUserStatus(user, false);
            this.router.navigate(['thank-you']);
          } else {
            this.authService.login(user.accessToken);
            this.authService.setUserStatus(user, false);

            this.router.navigate([
              'workspace',
              {outlets: {stages: this.getStage(user.applicationStatus)}},
            ]);
          }
        }
      },
      (error) => {
        this.isWaitingResponse = false;
        if (this.tries > 0) {
          this.tries--;
        } else {
          const queryParams = this.route.snapshot.queryParams;
          this.authService.logout();
          this.router.navigate([''], {queryParams});
        }
      }
    );
  }

  resendCode() {
    if (this.recaptchaV3ServiceSubscription) {
      this.recaptchaV3ServiceSubscription.unsubscribe();
    }

    this.recaptchaV3ServiceSubscription = this.recaptchaV3Service
      .execute('resend_code')
      .subscribe(
        (token) => {
          const initialUrl = this.browseStorage.getSession('initialUrl');

          this.newCodeWasSent = true;
          this.tries = 3;
          this.loginService.loginUser(
            this.loginModel.phone,
            token,
            false,
            initialUrl,
            (user) => {
              this.loginModel = user;
              this.loginModel.verificationCode = null;
              this.authService.waitForConfirmation(
                user.vid,
                user.phone
              );
            },
            () =>
              this.toastr.error(
                `${this.translateService.instant(
                  'FailedLogin'
                )}. ${this.translateService.instant('ContactSupport')}`
              )
          );

          document.getElementById('charCode1').focus();
        },
        () =>
          this.toastr.error(
            `${this.translateService.instant(
              'FailedLogin'
            )}. ${this.translateService.instant('ContactSupport')}`
          )
      );
  }

  private getStage(applicationStatus: string) {
    const stage = this.stagesNavigationService.workflowStages.find(x => x.applicationStatus === applicationStatus);
    return !!stage ? stage.stageOutlet : this.stagesNavigationService.workflowStages[0].stageOutlet;
  }
}
